import React from 'react';
import {
  DateField,
  RecordContextProvider,
  SelectField,
  TextField,
  useRecordContext,
} from 'react-admin';
import { get, keyBy } from 'lodash';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import Answer from '../Answer';
import { CustomParameters } from '../../questionParameters';
import applyChoices from '../../applyChoices';

const ValueField = ({
  extraParameters,
  boldFont = true,
}: {
  extraParameters?: CustomParameters;
  boldFont?: boolean;
}) => {
  const record = useRecordContext();

  const value: unknown = get(record, 'value');
  const sx = get(record, 'sx', {});

  if (extraParameters?.subDocumentSpecification) {
    if (extraParameters.multiple) {
      if (!Array.isArray(value)) return null;
      if (value.length === 0) return '-';

      const summarySpec = extraParameters.summaryField
        ? extraParameters.subDocumentSpecification
          .find(({ id }) => id === extraParameters.summaryField)
        : undefined;

      return (
        <Card variant="outlined">
          <Grid container bgcolor="background.paper">
            {value.map((item, index) => (
              <Grid
                key={`${extraParameters.id}-${index + 1}`}
                size={12}
              >
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ChevronRightIcon />}
                    sx={{
                      flexDirection: 'row-reverse',
                      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                        transform: 'rotate(90deg)',
                      },
                    }}
                  >
                    <Typography variant="h6">
                      {summarySpec ? (
                        <RecordContextProvider value={{ value: get(item, summarySpec.id ?? ''), sx: { fontWeight: 'normal' } }}>
                          <ValueField extraParameters={summarySpec} />
                        </RecordContextProvider>
                      ) : `Item #${index + 1}`}
                    </Typography>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails>
                    <Grid container spacing={6} padding={2}>
                      {extraParameters.subDocumentSpecification?.map((spec) => (
                        <RecordContextProvider value={{ value: get(item, spec.id ?? '') }} key={spec.id}>
                          <Grid
                            size={{
                              xs: 12,
                              md: 6,
                            }}
                          >
                            <Box display="flex" flexDirection="column">
                              <Typography variant="label">{spec.label}</Typography>
                              <ValueField extraParameters={spec} />
                            </Box>
                          </Grid>
                        </RecordContextProvider>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                {index + 1 !== value.length && <Divider />}
              </Grid>
            ))}
          </Grid>
        </Card>
      );
    }

    return (
      <Card variant="outlined">
        <Grid container spacing={6} padding={2}>
          {extraParameters.subDocumentSpecification?.map((spec) => (
            <RecordContextProvider value={{ value: get(value, spec.id ?? '') }} key={spec.id}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Typography variant="label">{spec.label}</Typography>
                  <ValueField extraParameters={spec} />
                </Box>
              </Grid>
            </RecordContextProvider>
          ))}
        </Grid>
      </Card>
    );
  }

  const choices = extraParameters?.options && applyChoices(
    extraParameters.options.map(({ id, label }) => ({ id, name: label })),
    undefined,
    extraParameters.choiceLabels,
  );

  if (extraParameters?.multiple) {
    if (!Array.isArray(value)) return null;

    if (choices) {
      const mapFields = keyBy(choices, 'id');
      const mappedValues = value.map((v: string) => get(mapFields, v, { name: v }).name);
      return (
        <TextField
          record={{ value: mappedValues.join(', ') }}
          sx={sx}
          source="value"
          emptyText="-"
          fontWeight={boldFont ? 'bold' : ''}
        />
      );
    }

    return (
      <TextField
        record={{ value: value.join(', ') }}
        sx={sx}
        source="value"
        emptyText="-"
        fontWeight={boldFont ? 'bold' : ''}
      />
    );
  }

  if (extraParameters?.type === 'date') {
    return (
      <DateField
        record={{ value }}
        sx={sx}
        source="value"
        emptyText="-"
        fontWeight={boldFont ? 'bold' : ''}
      />
    );
  }

  return choices ? (
    <SelectField
      choices={choices}
      record={{ value }}
      sx={sx}
      source="value"
      emptyText="-"
      fontWeight={boldFont ? 'bold' : ''}
    />
  ) : (
    <TextField
      record={{ value }}
      sx={sx}
      source="value"
      emptyText="-"
      fontWeight={boldFont ? 'bold' : ''}
    />
  );
};

const Custom = ({ extraParameters }: {
  extraParameters?: CustomParameters;
}) => {
  if (!extraParameters?.id) return null;

  return (
    <Answer
      label={extraParameters.label ?? extraParameters.inputLabel ?? ''}
      source={`custom.${extraParameters.id}`}
      datapoint={null}
      identificationInfo={undefined}
      isComplexType={!!extraParameters?.subDocumentSpecification}
    >
      <ValueField extraParameters={extraParameters} />
    </Answer>
  );
};

Custom.ValueField = ValueField;

Custom.Nested = ({ extraParameters }: { extraParameters?: CustomParameters }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const record = useRecordContext();

  if (!extraParameters?.id) return null;

  const value = get(record, `custom.${extraParameters.id}`);
  const sx = get(record, 'sx', {});

  return (
    <Grid
      size={{
        xs: 12,
        md: 3,
      }}
    >
      <Box display="flex" flexDirection="column">
        <Typography variant="label">{extraParameters.label ?? extraParameters.inputLabel ?? ''}</Typography>
        <RecordContextProvider value={{ value, sx }}>
          <ValueField extraParameters={extraParameters} boldFont={false} />
        </RecordContextProvider>
      </Box>
    </Grid>
  );
};

export default Custom;
